import { z } from 'zod';

import {
  CLAIM_STATUS,
  CONFIDENTIALITY,
  CONTEXT_MATURITY_STAGES,
  CONTEXT_TYPES,
  ENTITY_STATUS,
  PACKAGE_MATURITY_STAGES,
  TARGET_LEVELS,
} from '../constants/enums';

export const strategyWindowSchema = z.enum(['now', '7d', '30d', 'quarter', 'custom']);
export const strategyMovementStatusSchema = z.enum([
  'unknown',
  'on_track',
  'at_risk',
  'blocked',
  'stale',
]);
export const strategyBlockerKindSchema = z.enum([
  'missing_key_results',
  'missing_measurement',
  'open_decision',
  'missing_owner_context',
]);

export const strategyBetSummarySchema = z.object({
  id: z.string().min(1),
  package_type: z.literal('bet'),
  target_level: z.enum(TARGET_LEVELS),
  name: z.string().min(1),
  status: z.string().min(1),
  maturity_stage: z.enum(PACKAGE_MATURITY_STAGES),
  thesis: z.string().nullable(),
});

export const strategicContextSummarySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  context_type: z.enum(CONTEXT_TYPES),
  host_type: z.string().nullable(),
  host_package_id: z.string().min(1),
  maturity_stage: z.enum(CONTEXT_MATURITY_STAGES),
  status: z.string().min(1),
  confidentiality: z.enum(CONFIDENTIALITY),
});

export const strategyObjectiveSummarySchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  description: z.string().nullable(),
  period: z.string().nullable(),
  owner_user_id: z.string().nullable(),
  status: z.enum(ENTITY_STATUS),
  strategic_binding_id: z.string().min(1),
});

export const strategyOwnerContextSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  context_type: z.enum(CONTEXT_TYPES),
  status: z.string().min(1),
  binding_id: z.string().min(1),
});

export const strategyMetricSummarySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  unit: z.string().nullable(),
});

export const strategyMeasurementSummarySchema = z.object({
  id: z.string().min(1),
  metric_id: z.string().min(1),
  measured_at: z.string().min(1),
  value: z.number(),
  source_system: z.string().nullable(),
  source_artifact_id: z.string().nullable(),
});

export const strategyKeyResultSummarySchema = z.object({
  id: z.string().min(1),
  objective_id: z.string().min(1),
  title: z.string().min(1),
  target_value: z.number().nullable(),
  current_value: z.number().nullable(),
  unit: z.string().nullable(),
  metric_id: z.string().nullable(),
  status: z.enum(ENTITY_STATUS),
  metric: strategyMetricSummarySchema.nullable(),
  latest_measurement: strategyMeasurementSummarySchema.nullable(),
  progress_ratio: z.number().nullable(),
  movement_status: strategyMovementStatusSchema,
});

export const strategyArtifactSummarySchema = z.object({
  id: z.string().min(1),
  artifact_type: z.enum(['deliverable', 'outcome_record']),
  title: z.string().min(1),
  status: z.string().min(1),
});

export const strategyDecisionSummarySchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  status: z.string().min(1),
  decision_type: z.string().nullable(),
});

export const strategyClaimSummarySchema = z.object({
  id: z.string().min(1),
  claim_type: z.string().min(1),
  title: z.string().min(1),
  status: z.enum(CLAIM_STATUS),
  confidence_score: z.number().nullable(),
  accepted_evidence_count: z.number().int().min(0),
});

export const strategyBlockerSchema = z.object({
  kind: strategyBlockerKindSchema,
  subject_type: z.enum(['objective', 'key_result', 'decision']),
  subject_id: z.string().min(1),
  label: z.string().min(1),
});

export const strategyCommitmentSummarySchema = z.object({
  objective: strategyObjectiveSummarySchema,
  owner_contexts: z.array(strategyOwnerContextSchema),
  key_results: z.array(strategyKeyResultSummarySchema),
  deliverables: z.array(strategyArtifactSummarySchema),
  decisions: z.array(strategyDecisionSummarySchema),
  blockers: z.array(strategyBlockerSchema),
  movement_status: strategyMovementStatusSchema,
  progress_ratio: z.number().nullable(),
});

export const strategyPrioritizedPackageSchema = z.object({
  edge_id: z.string().min(1),
  source_package_id: z.string().min(1),
  target_package: z.object({
    id: z.string().min(1),
    package_type: z.string().min(1),
    target_level: z.string().min(1),
    name: z.string().min(1),
    status: z.string().min(1),
  }),
});

export const strategyExecutionSummarySchema = z.object({
  expected_outcomes: z.number().int().min(0),
  commitments: z.number().int().min(0),
  key_results: z.number().int().min(0),
  prioritized_packages: z.number().int().min(0),
  deliverables: z.number().int().min(0),
  decisions: z.number().int().min(0),
  blockers: z.number().int().min(0),
  stale_commitments: z.number().int().min(0),
});

export const strategyExecutionPacketSchema = z
  .object({
    packet_type: z.literal('strategy_execution_map'),
    version: z.literal('v3.strategy-execution.1'),
    generated_at: z.string().min(1),
    window: strategyWindowSchema,
    strategic_context: strategicContextSummarySchema,
    bet: strategyBetSummarySchema,
    expected_outcomes: z.array(strategyObjectiveSummarySchema),
    commitments: z.array(strategyCommitmentSummarySchema),
    prioritized_packages: z.array(strategyPrioritizedPackageSchema),
    claims: z.array(strategyClaimSummarySchema),
    summary: strategyExecutionSummarySchema,
  })
  .superRefine((packet, ctx) => {
    if (packet.strategic_context.context_type !== 'strategy') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'context must be strategy' });
    }
    if (packet.strategic_context.host_type !== 'package') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'strategy context must be package-hosted' });
    }
    if (packet.bet.target_level !== 'strategy') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'bet package must target strategy' });
    }
    if (packet.strategic_context.host_package_id !== packet.bet.id) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'strategy context must host bet package' });
    }
    for (const edge of packet.prioritized_packages) {
      if (edge.source_package_id !== packet.bet.id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'prioritized edge must originate at bet' });
      }
    }

    const objectiveIds = new Set(packet.expected_outcomes.map((objective) => objective.id));
    for (const commitment of packet.commitments) {
      const objectiveId = commitment.objective.id;
      if (!objectiveIds.has(objectiveId)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'commitment objective must be an expected outcome' });
      }
      for (const kr of commitment.key_results) {
        if (kr.objective_id !== objectiveId) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'key result objective mismatch' });
        }
        if (kr.metric_id !== null && kr.metric?.id !== kr.metric_id) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'key result metric mismatch' });
        }
        if (kr.latest_measurement && kr.latest_measurement.metric_id !== kr.metric_id) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'measurement metric mismatch' });
        }
      }
    }

    const summary = packet.summary;
    const keyResultCount = packet.commitments.reduce((sum, item) => sum + item.key_results.length, 0);
    const deliverableCount = packet.commitments.reduce((sum, item) => sum + item.deliverables.length, 0);
    const decisionCount = packet.commitments.reduce((sum, item) => sum + item.decisions.length, 0);
    const blockerCount = packet.commitments.reduce((sum, item) => sum + item.blockers.length, 0);
    const staleCount = packet.commitments.filter((item) => item.movement_status === 'stale').length;
    if (
      summary.expected_outcomes !== packet.expected_outcomes.length ||
      summary.commitments !== packet.commitments.length ||
      summary.key_results !== keyResultCount ||
      summary.prioritized_packages !== packet.prioritized_packages.length ||
      summary.deliverables !== deliverableCount ||
      summary.decisions !== decisionCount ||
      summary.blockers !== blockerCount ||
      summary.stale_commitments !== staleCount
    ) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'summary counts must match packet contents' });
    }
  });

export type StrategyExecutionPacket = z.infer<typeof strategyExecutionPacketSchema>;
